// Продажи сайта six-reasons.com (Яндекс.КИТ): обзор + выгрузка в Excel.
import { api } from "./client.js";

export interface SalesSummary {
  placed_orders: number;
  placed_revenue: number;
  buyout_orders: number;
  buyout_revenue: number;
  buyout_units: number;
  aov: number | null;
  intransit_orders: number;
  intransit_prepaid_orders: number;
  intransit_revenue: number;
  cancelled_orders: number;
  refunded_orders: number;
  refunded_revenue: number;
  first_order_day: string | null;
  last_order_day: string | null;
  first_buyout_day: string | null;
  last_buyout_day: string | null;
}

export interface DayPoint { day: string; placed: number; buyout: number; buyout_revenue: number; }
export interface MonthPoint { month: string; placed: number; buyout: number; buyout_revenue: number; }
export interface ProductRow { product: string; orders: number; units: number; revenue: number; }
export interface SizeRow { size: string; units: number; revenue: number; }
export interface ColorRow { color: string; units: number; revenue: number; }
export interface PaymentRow { method: string; orders: number; revenue: number; }

export interface AttrSegment {
  spend: number;
  orders: number;
  revenue: number;
  roas: number | null;
  drr: number | null;
}

export interface SalesAttribution {
  sr: AttrSegment;
  agency: AttrSegment;
  total: AttrSegment;
  metrika_has_data: boolean;
}

export interface AdEfficiency {
  total_buyout: number;
  total_spend: number;
  mer: number | null;
  nc_buyout: number;
  nc_orders: number;
  nc_roas: number | null;
}

export interface CampaignAttrRow {
  campaign_id: number;
  name: string;
  is_sr: boolean;
  spend: number;
  clicks: number;
  impressions: number;
  buyout_orders: number;
  buyout_revenue: number;
  cp_buyout: number | null;
  roas: number | null;
}

export interface AttrModelSeg { orders: number; revenue: number; roas: number | null }
export interface AttrModel { sr: AttrModelSeg; agency: AttrModelSeg; total: AttrModelSeg }
export interface AttributionModels {
  first_click: AttrModel;
  last_click: AttrModel;
  linear: AttrModel;
  spend_sr: number;
  spend_agency: number;
}

export interface SalesOverview {
  period: { from: string; to: string };
  summary: SalesSummary;
  efficiency: AdEfficiency;
  byDay: DayPoint[];
  byMonth: MonthPoint[];
  byProduct: ProductRow[];
  bySize: SizeRow[];
  byColor: ColorRow[];
  byPayment: PaymentRow[];
  attribution: SalesAttribution;
  attributionModels: AttributionModels;
  campaigns: CampaignAttrRow[];
}

export interface SalesRange { from?: string; to?: string }

function qs(range: SalesRange): string {
  const p = new URLSearchParams();
  if (range.from) p.set("from", range.from);
  if (range.to) p.set("to", range.to);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function fetchSalesOverview(range: SalesRange): Promise<SalesOverview> {
  return api<SalesOverview>(`/api/sales/overview${qs(range)}`);
}

export interface ProductFinanceRow {
  product: string;
  placed_orders: number;
  placed_revenue: number;
  buyout_orders: number;
  buyout_revenue: number;
  buyout_units: number;
  buyout_rate: number | null;
  cogs: number;
  margin: number;
  margin_pct: number | null;
}

export function fetchProductFinance(range: SalesRange): Promise<ProductFinanceRow[]> {
  return api<ProductFinanceRow[]>(`/api/sales/products${qs(range)}`);
}

export interface ItemDetailRow {
  sku: string;
  order_number: number;
  order_date: string;
  buyout_date: string | null;
  cancel_date: string | null;
  qty: number;
  amount: number;
  status: string;
  is_buyout: boolean;
}

export function fetchSalesItems(range: SalesRange): Promise<ItemDetailRow[]> {
  return api<ItemDetailRow[]>(`/api/sales/items${qs(range)}`);
}

export interface PnL {
  from: string;
  to: string;
  buyout_revenue: number;
  online_revenue: number;
  cogs: number;
  delivery: number;
  acquiring: number;
  tax: number;
  ad_spend_sr: number;
  ad_spend_agency: number;
  contribution: number;
  profit_sr: number;
  profit_total: number;
  acquiring_rate: number;
  tax_rate: number;
}

export interface PnLRates { acquiring?: number; tax?: number } // доли (0.025, 0.06)

export function fetchPnL(range: SalesRange, rates?: PnLRates): Promise<PnL> {
  const p = new URLSearchParams(qs(range).replace(/^\?/, ""));
  if (rates?.acquiring != null && Number.isFinite(rates.acquiring)) p.set("acquiring", String(rates.acquiring));
  if (rates?.tax != null && Number.isFinite(rates.tax)) p.set("tax", String(rates.tax));
  const s = p.toString();
  return api<PnL>(`/api/sales/pnl${s ? `?${s}` : ""}`);
}

// Скачивание .xlsx (бинарь, мимо JSON-клиента)
export async function downloadSalesXlsx(range: SalesRange): Promise<void> {
  const res = await fetch(`/api/sales/export.xlsx${qs(range)}`, { credentials: "include" });
  if (!res.ok) throw new Error(`Экспорт не удался: HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `sales-six-reasons-${range.from ?? "all"}_${range.to ?? "now"}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
